"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Lock, EyeOff, FileText, BellRing, Mail, Phone, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
const policies = [{
  id: "data-collection",
  title: "Information we collect during consultations",
  icon: FileText,
  summary: "Only what is required to assess and plan your renovation.",
  details: "When you request a renovation consultation or estimate, we collect basic contact details (your name, email address, phone number, and physical Decatur-area address) along with your project notes, target timelines, and room dimensions. We use this information solely to prepare initial cost guides, conduct on-site visits, and coordinate project planning."
}, {
  id: "consultation-forms",
  title: "How your project inquiries are protected",
  icon: Lock,
  summary: "Secure digital handling from estimate to contract completion.",
  details: "Every form submitted on our website is transmitted via encrypted connections. Your drawings, room photos, architectural sketches, and budget parameters are stored within access-controlled project files accessible only by our lead builders and design coordinators."
}, {
  id: "non-disclosure",
  title: "Zero third-party selling or sharing",
  icon: EyeOff,
  summary: "We never sell, rent, or trade your personal information.",
  details: "Decatur Heritage Renovations operates strictly as a residential remodeling contractor. We do not sell homeowner lists, marketing profiles, or contact data to third-party advertisers. Trusted trade partners (such as structural engineers or city permit offices) only receive property specifics strictly necessary for project execution and local code compliance."
}, {
  id: "communication",
  title: "Communication and messaging preferences",
  icon: BellRing,
  summary: "You decide how and when we contact you about your home.",
  details: "We contact you via phone or email exclusively regarding scheduled site consultations, active estimates, architectural reviews, or construction milestones. You can adjust your communication preferences or request removal from our contact logs at any time by sending a short note to our office team."
}];
const pillars = [{
  icon: ShieldCheck,
  title: "Strict confidentiality",
  description: "Your home floor plans, access codes, and budget sheets stay private."
}, {
  icon: Lock,
  title: "Encrypted inquiries",
  description: "Estimate requests and uploaded home photos transfer over secure channels."
}, {
  icon: EyeOff,
  title: "No sales lists",
  description: "We never distribute or monetize your contact information."
}];
export default function Privacy() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="privacy-policy" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 border-border bg-secondary text-secondary-foreground text-xs uppercase tracking-widest font-semibold px-3 py-1 rounded-sm">
            Trust & Security
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight">
            Your home plans stay private.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            We treat your project ideas, drawings, and contact details with the same care we put into building your home.
          </p>
        </div>

        {/* 3 Quick Pillar Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.title} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card/85 backdrop-blur-md rounded-xl p-6 shadow-xs hover:border-border transition-[border-color,box-shadow]">
                  <div className="size-10 rounded-md bg-secondary flex items-center justify-center text-foreground mb-4">
                    <Icon className="size-5" />
                  </div>
                  <CardTitle className="font-serif text-lg font-semibold text-card-foreground">
                    {pillar.title}
                  </CardTitle>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {pillar.description}
                  </p>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Main Content Area - Single Column Reader */}
        <div className="mt-12 sm:mt-16 mx-auto max-w-3xl">
          <div className="border border-border bg-card rounded-xl p-6 sm:p-8 shadow-xs">
            <div className="border-b border-border pb-6 mb-6">
              <h2 className="font-serif text-xl sm:text-2xl font-semibold text-card-foreground">
                Our privacy commitments
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Last updated for Decatur, Georgia homeowners and renovation clients.
              </p>
            </div>

            {/* Accordions */}
            <Accordion type="single" collapsible defaultValue="data-collection" className="w-full space-y-3">
              {policies.map((policy, index) => {
              const PolicyIcon = policy.icon;
              return <AccordionItem key={policy.id} value={policy.id} data-index={index} className="border border-border rounded-lg px-4 bg-background/50 overflow-hidden">
                    <AccordionTrigger className="py-4 hover:no-underline group">
                      <div className="flex items-center gap-3 text-left">
                        <div className="size-8 rounded-md bg-secondary flex items-center justify-center text-foreground shrink-0">
                          <PolicyIcon className="size-4" />
                        </div>
                        <div>
                          <div className="font-serif font-medium text-foreground text-base group-hover:text-foreground">
                            {policy.title}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5 hidden sm:block">
                            {policy.summary}
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-1 pb-4 text-muted-foreground text-sm leading-relaxed border-t border-border/50">
                      {policy.details}
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>
          </div>

          {/* Contact Verification Box */}
          <div className="mt-8 border border-border bg-secondary/50 rounded-xl p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <h3 className="font-serif text-lg font-semibold text-foreground">
                  Questions about our data handling?
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Contact our Decatur office directly for record updates or privacy inquiries.
                </p>
                <div className="mt-4 flex flex-wrap gap-y-2 gap-x-6 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="size-3.5" />
                    {"Decatur, Georgia 30030"}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Phone className="size-3.5" />
                    <a href="tel:+14708479076" className="hover:underline text-foreground">
                      {"(470) 847-9076"}
                    </a>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Mail className="size-3.5" />
                    <a href="mailto:serviceleadalerts@gmail.com" className="hover:underline text-foreground">
                      {"serviceleadalerts@gmail.com"}
                    </a>
                  </span>
                </div>
              </div>
              <div className="shrink-0">
                <Button asChild variant="outline" className="border-border text-foreground bg-card hover:bg-muted">
                  <Link to="/ContactPage">
                    Contact our team
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}